import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import PostComposer from "@/components/PostComposer";
import VentCard from "@/components/VentCard";
import TrendingStrip from "@/components/TrendingStrip";
import { Loader2 } from "lucide-react";

export default function Feed({ user }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("recent");

  const load = async (cat = category, srt = sort) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/posts`, {
        params: { category: cat, user_id: user.id, sort: srt },
      });
      setPosts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(category, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sort]);

  const onPosted = (newPost) => setPosts((prev) => [newPost, ...prev]);
  const onUpdate = (updated) => setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  const onRemove = (id) => setPosts((prev) => prev.filter((p) => p.id !== id));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10">
      <div className="lg:col-span-8 space-y-6">
        <PostComposer user={user} onPosted={onPosted} />
        <TrendingStrip user={user} />

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-wrap gap-2" data-testid="category-filter">
            {["all", ...CATEGORIES].map((c) => {
              const active = category === c;
              return (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`filter-${c.toLowerCase()}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all"
                  style={{
                    background: active ? "var(--exhale-accent)" : "transparent",
                    color: active ? "#0C0B0E" : "var(--exhale-text-2)",
                    border: `1px solid ${active ? "var(--exhale-accent)" : "var(--exhale-border)"}`,
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="flex gap-1 rounded-full p-1 border" style={{ borderColor: "var(--exhale-border)" }}>
            {["recent", "hot"].map((s) => (
              <button
                key={s}
                onClick={() => setSort(s)}
                data-testid={`sort-${s}`}
                className="px-3 py-1 rounded-full text-xs font-medium capitalize"
                style={{
                  background: sort === s ? "var(--exhale-surface-hover)" : "transparent",
                  color: sort === s ? "var(--exhale-accent-2)" : "var(--exhale-text-muted)",
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="feed-loading">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--exhale-accent)" }} />
          </div>
        ) : posts.length === 0 ? (
          <div
            className="text-center py-16 rounded-2xl border"
            style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
            data-testid="feed-empty"
          >
            <p className="font-display text-2xl mb-2 ember-text">Silence.</p>
            <p style={{ color: "var(--exhale-text-2)" }}>
              Nobody has vented here yet. Be the first to exhale.
            </p>
          </div>
        ) : (
          posts.map((p) => <VentCard key={p.id} post={p} user={user} onUpdate={onUpdate} onRemove={onRemove} />)
        )}
      </div>

      <aside className="lg:col-span-4 space-y-6">
        <div
          className="rounded-2xl border p-6 sticky top-24"
          style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
          data-testid="sidebar-manifesto"
        >
          <div className="text-xs font-medium tracking-widest uppercase mb-2" style={{ color: "var(--exhale-text-muted)" }}>
            The manifesto
          </div>
          <h4 className="font-display text-xl font-bold mb-3" style={{ color: "var(--exhale-text)" }}>
            No filters. No fake positivity. Just raw exhale.
          </h4>
          <ul className="space-y-2 text-sm" style={{ color: "var(--exhale-text-2)" }}>
            <li>· Fully anonymous handles</li>
            <li>· Swear, rant, cry — whatever you need</li>
            <li>· React with empathy, not judgment</li>
            <li>· Log your mood, build a streak</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
